/* eslint-disable @typescript-eslint/no-explicit-any */

import DOMPurify from 'dompurify';

import { extLnkLiiaspVerify, extLnkMyPostSafeDrop } from 'config/track-config';
import { CustomerAuthLevel } from 'models/Customer';
import { EnhancedWindow } from 'models/Window';

export const AppLocation = {
  get href() {
    return window.location.href;
  },
  get search() {
    return window.location.search;
  },
  assign: (url: string | URL) => window.location.assign(url),
  replace: (url: string | URL) => window.location.replace(url),
};

export const AppWindow: EnhancedWindow = window;

// Only get known token from URL
export const getKnownTokenQueryFromUrl = () => {
  const queryParams = new URLSearchParams(AppLocation.search);
  const knownToken = queryParams.get('token') || '';
  const cleanKnownToken = DOMPurify.sanitize(knownToken, { USE_PROFILES: { html: true } });
  const queryString = cleanKnownToken ? `?token=${cleanKnownToken}` : '';
  return queryString;
};

export const reportNewRelicError = (err?: unknown) => {
  const newRelic = AppWindow.newrelic;
  if (newRelic?.noticeError) newRelic.noticeError(err);
};

const CACHE_MAX_AGE = 300000; //5min
export const CACHE_SEARCHED_ARTICLES = 'article-added';

const _getItem = (key: string): { data: any; expireTime: number } | null => {
  try {
    const storedData = window.localStorage.getItem(key);
    if (!storedData) return null;
    const { expireTime, data } = JSON.parse(storedData);
    return { data, expireTime };
  } catch (error) {
    return null;
  }
};

export const setCacheItem = (key: string, value: any, preserveExpireTime = false) => {
  const { expireTime: previousExpireTime } = _getItem(key) ?? {};
  const result = {
    data: value,
    expireTime: Date.now() + CACHE_MAX_AGE,
  };

  if (preserveExpireTime && previousExpireTime) result.expireTime = previousExpireTime;
  window.localStorage.setItem(key, JSON.stringify(result));
};

export const getCacheItem = (key: string) => {
  const { data, expireTime } = _getItem(key) ?? {};
  if (!data) return null;
  if (expireTime && expireTime <= Date.now()) return deleteCacheItem(key);
  return data;
};

export const deleteCacheItem = (key: string) => {
  return window.localStorage.removeItem(key);
};

export const getAtlLink = (authLevel: CustomerAuthLevel, trackingNumber: string) => {
  const safeDropUrl = `${extLnkMyPostSafeDrop}/${trackingNumber}`;

  // Known token should take precedence; go to safe drop with known token
  const knownTokenQuery = getKnownTokenQueryFromUrl();
  if (knownTokenQuery) return safeDropUrl + knownTokenQuery;

  // If anon go to liiasp
  if (authLevel === CustomerAuthLevel.ANONYMOUS) return extLnkLiiaspVerify(trackingNumber);

  return safeDropUrl;
};
